import { ref, computed, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'

// Accent color palette (full Tailwind shade range for --color-blue-* override)
const ACCENT_COLORS = {
  blue: {
    label: 'Mavi',
    50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd',
    400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8',
    800: '#1e40af', 900: '#1e3a8a', 950: '#172554',
  },
  purple: {
    label: 'Mor',
    50: '#faf5ff', 100: '#f3e8ff', 200: '#e9d5ff', 300: '#d8b4fe',
    400: '#c084fc', 500: '#a855f7', 600: '#9333ea', 700: '#7e22ce',
    800: '#6b21a8', 900: '#581c87', 950: '#3b0764',
  },
  indigo: {
    label: 'Indigo',
    50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 300: '#a5b4fc',
    400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca',
    800: '#3730a3', 900: '#312e81', 950: '#1e1b4b',
  },
  teal: {
    label: 'Teal',
    50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4',
    400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e',
    800: '#115e59', 900: '#134e4a', 950: '#042f2e',
  },
  emerald: {
    label: 'Yeşil',
    50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7',
    400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857',
    800: '#065f46', 900: '#064e3b', 950: '#022c22',
  },
  rose: {
    label: 'Gül',
    50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 300: '#fda4af',
    400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c',
    800: '#9f1239', 900: '#881337', 950: '#4c0519',
  },
  amber: {
    label: 'Amber',
    50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d',
    400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309',
    800: '#92400e', 900: '#78350f', 950: '#451a03',
  },
  orange: {
    label: 'Turuncu',
    50: '#fff7ed', 100: '#ffedd5', 200: '#fed7aa', 300: '#fdba74',
    400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c',
    800: '#9a3412', 900: '#7c2d12', 950: '#431407',
  },
}

// Singleton state (module-level)
const currentTheme = ref('light')
const accentColor = ref('blue')
let mediaQuery = null
let mediaListener = null

const resolvedTheme = computed(() => {
  if (currentTheme.value === 'auto') {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  }
  return currentTheme.value
})

function applyTheme() {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  const isDark = resolvedTheme.value === 'dark'

  // Add transition class for smooth switch
  html.classList.add('theme-transitioning')

  if (isDark) {
    html.classList.add('dark')
  } else {
    html.classList.remove('dark')
  }

  // Update meta theme-color
  const metaTheme = document.querySelector('meta[name="theme-color"]')
  if (metaTheme) {
    metaTheme.setAttribute('content', isDark ? '#1f2937' : ACCENT_COLORS[accentColor.value]?.['600'] || '#2563eb')
  }

  // Remove transition class after animation completes
  setTimeout(() => {
    html.classList.remove('theme-transitioning')
  }, 350)
}

function applyAccent(name) {
  if (typeof document === 'undefined') return
  const colors = ACCENT_COLORS[name]
  if (!colors) return

  // Custom accent variables
  const vars = {
    '--accent-50': colors['50'],
    '--accent-100': colors['100'],
    '--accent-200': colors['200'],
    '--accent-400': colors['400'],
    '--accent-500': colors['500'],
    '--accent-600': colors['600'],
    '--accent-700': colors['700'],
  }

  // Override Tailwind 4's --color-blue-* variables so ALL bg-blue-*, text-blue-*, etc. follow accent
  const shades = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950']
  shades.forEach(shade => {
    if (colors[shade]) {
      vars[`--color-blue-${shade}`] = colors[shade]
    }
  })

  const html = document.documentElement
  Object.entries(vars).forEach(([key, value]) => {
    html.style.setProperty(key, value)
  })

  // Persist accent vars for flash-prevention script
  localStorage.setItem('spt-accent', JSON.stringify(vars))

  // Update meta theme-color
  const metaTheme = document.querySelector('meta[name="theme-color"]')
  if (metaTheme && resolvedTheme.value !== 'dark') {
    metaTheme.setAttribute('content', colors['600'])
  }
}

function setTheme(mode) {
  if (!['light', 'dark', 'auto'].includes(mode)) return
  currentTheme.value = mode
  localStorage.setItem('spt-theme', mode)
  setupMediaListener()
  applyTheme()
}

function setAccentColor(name) {
  if (!ACCENT_COLORS[name]) return
  accentColor.value = name
  localStorage.setItem('spt-accent-name', name)
  applyAccent(name)
}

function setupMediaListener() {
  // Clean up previous listener
  if (mediaQuery && mediaListener) {
    mediaQuery.removeEventListener('change', mediaListener)
    mediaListener = null
  }

  // Only listen when in auto mode
  if (currentTheme.value === 'auto' && typeof window !== 'undefined') {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaListener = () => applyTheme()
    mediaQuery.addEventListener('change', mediaListener)
  }
}

function initTheme() {
  if (typeof window === 'undefined') return

  // 1. Try to read from Inertia page props (server-side user preference)
  try {
    const page = usePage()
    const user = page.props?.auth?.user
    if (user) {
      if (user.theme_preference && ['light', 'dark', 'auto'].includes(user.theme_preference)) {
        currentTheme.value = user.theme_preference
        localStorage.setItem('spt-theme', user.theme_preference)
      }
      const savedAccent = user.custom_settings?.accent_color
      if (savedAccent && ACCENT_COLORS[savedAccent]) {
        accentColor.value = savedAccent
        localStorage.setItem('spt-accent-name', savedAccent)
      }
    }
  } catch {
    // Not in Inertia context, fallback to localStorage
  }

  // 2. Fallback to localStorage
  const storedTheme = localStorage.getItem('spt-theme')
  if (storedTheme && ['light', 'dark', 'auto'].includes(storedTheme)) {
    currentTheme.value = storedTheme
  }

  const storedAccent = localStorage.getItem('spt-accent-name')
  if (storedAccent && ACCENT_COLORS[storedAccent]) {
    accentColor.value = storedAccent
  }

  // 3. Apply
  setupMediaListener()
  applyTheme()
  applyAccent(accentColor.value)
}

export function useTheme() {
  return {
    currentTheme,
    resolvedTheme,
    accentColor,
    setTheme,
    setAccentColor,
    initTheme,
    ACCENT_COLORS,
  }
}
